import { Timestamp } from 'firebase/firestore';

export interface TaskDocument {
  id: string;
  name: string;
  originalName?: string;
  url: string;
  uploadedAt: Date | Timestamp | any; // Támogatja a Date, Timestamp és egyéb formátumokat
  type?: string;
  size?: number;
}

/** Szervezetenként testreszabható feladat-státusz. A category a beépített logika (kész, lejárt, KPI) miatt marad. */
export type TaskStatusCategory = 'todo' | 'in_progress' | 'completed' | 'cancelled';
export interface TaskStatusDef {
  id: string;
  name: string;
  color: string; // hex
  category: TaskStatusCategory;
  order: number;
}
export const DEFAULT_TASK_STATUSES: TaskStatusDef[] = [
  { id: 'todo', name: 'Teendő', color: '#9ca3af', category: 'todo', order: 0 },
  { id: 'in_progress', name: 'Folyamatban', color: '#0ea5e9', category: 'in_progress', order: 1 },
  { id: 'completed', name: 'Kész', color: '#10b981', category: 'completed', order: 2 },
  { id: 'cancelled', name: 'Törölve', color: '#f43f5e', category: 'cancelled', order: 3 }
];

export interface Subtask {
  id: string;
  title: string;
  done: boolean;
  createdAt?: Date | any;
  completedAt?: Date | any;
}

export type RecurrenceFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';

/** Ismétlődés (karbantartási ciklus) – a sablon feladaton. */
export interface TaskRecurrence {
  freq: RecurrenceFreq;
  interval: number;          // minden N nap/hét/hónap/év
  weekdays?: number[];       // heti: 1 = hétfő … 7 = vasárnap
  dayOfMonth?: number;       // havi / éves: hónap napja
  month?: number;            // éves: 1–12
  nextRunAt: Date;           // következő esedékesség (ez lesz a példány határideje)
  anchor?: Date;             // első esedékesség (heti intervallum számításához)
  leadDays?: number;         // ennyi nappal az esedékesség előtt jön létre a feladat
  endDate?: Date;
  createWorksheet?: boolean; // munkalap-vázlat is készüljön
  workType?: 'installation' | 'maintenance' | 'repair' | 'inspection' | 'other';
  active: boolean;
  lastRunAt?: Date;
  count?: number;            // eddig létrehozott példányok
}

export interface Task {
  id: string;
  taskNumber: string; // Egyedi feladat szám, pl. TASK-001
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
  statusId?: string; // egyedi státusz azonosító (TaskStatusDef.id); ha nincs, a status = statusId
  assignedTo?: string;
  assignedToName?: string;
  createdBy: string;
  createdByName?: string;
  dueDate?: Date;
  completedDate?: Date;
  projectId?: string;
  customerId?: string;
  customerName?: string;
  estimatedHours?: number;
  manualHours?: number; // Manuálisan beírt órák
  actualHours?: number; // Összes óra (manualHours + időmérős órák)
  tags: string[];
  attachments?: string[]; // Deprecated - használd a documents-t
  documents: TaskDocument[]; // Csatolt dokumentumok
  comments?: TaskComment[];
  customFields?: Record<string, any>; // Egyedi mezők
  subtasks?: Subtask[]; // Alfeladatok / checklist
  isRecurringTemplate?: boolean;   // ismétlődő sablon (nem jelenik meg a listákban)
  recurrence?: TaskRecurrence;
  recurrenceParentId?: string;     // ebből a sablonból jött létre
  recurrenceOccurrence?: Date;
  worksheetId?: string;            // automatikusan létrehozott munkalap
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TaskCommentAttachment {
  id: string;
  name: string;
  originalName: string;
  url: string;
  type: string; // MIME type
  size: number; // bytes
  uploadedAt: Date;
}

export interface TaskComment {
  id: string;
  taskId: string;
  userId: string;
  userName: string;
  message: string;
  attachments?: TaskCommentAttachment[];
  createdAt: Date;
  updatedAt?: Date;
}

export interface TimeEntry {
  id: string;
  taskId: string;
  userId: string;
  userName: string;
  description?: string;
  startTime: Date;
  endTime?: Date; // Ha null, akkor még fut az időmérő
  duration?: number; // Másodpercben (endTime - startTime)
  isBillable?: boolean;
  isPaused?: boolean; // Szünetel-e az időmérő
  pausedAt?: Date; // Mikor lett szüneteltetve
  pausedDuration?: number; // Összesen hány másodperc volt szüneteltetve
  subtaskId?: string;      // ha alfeladatra indították
  subtaskTitle?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTaskData {
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
  statusId?: string;
  assignedTo?: string;
  assignedToName?: string;
  dueDate?: Date;
  projectId?: string;
  customerId?: string;
  estimatedHours?: number;
  tags: string[];
  attachments?: string[]; // Deprecated - használd a documents-t
  documents: TaskDocument[]; // Csatolt dokumentumok
  customFields?: Record<string, any>; // Egyedi mezők
  subtasks?: Subtask[];
  isRecurringTemplate?: boolean;
  recurrence?: TaskRecurrence | null;
  // taskNumber automatikusan generálódik, nem kell megadni
}

export interface TaskFormData extends CreateTaskData {
  id?: string;
  taskNumber?: string; // Szerkesztésnél megjelenhet
  manualHours?: number; // Manuálisan beírt órák
  actualHours?: number; // Összes óra (csak olvasható, számított)
  completedDate?: Date;
  customerName?: string;
}

export interface TaskFilters {
  status?: string;
  priority?: string;
  assignedTo?: string;
  customer?: string;
  project?: string;
  dueDateRange?: {
    start?: Date;
    end?: Date;
  };
  tags?: string[];
  taskNumber?: string; // Keresés feladat szám alapján
}

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
} 